<template>
  <div class="new-energy-case-container">
    <!-- 顶部横幅 -->
    <div class="banner-section">
      <div class="banner-content">
        <h1 class="banner-title">{{ $t('caseStudies.newEnergyCases') }}</h1>
        <p class="banner-subtitle">{{ $t('newEnergy.bannerSubtitle') }}</p>
        <div class="banner-desc">
          {{ $t('newEnergy.bannerDescription') }}
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>{{ $t('common.loading') }}</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <h3>{{ $t('common.error') }}</h3>
      <p>{{ error }}</p>
      <button @click="loadCaseDetail" class="retry-btn">{{ $t('common.reset') }}</button>
    </div>

    <!-- 案例详情 -->
    <div v-else class="case-detail">
      <div class="container">
        <div class="case-header">
          <h2 class="case-title">{{ caseDetail.title }}</h2>
          <div class="case-meta">
            <span class="case-category">{{ caseDetail.category }}</span>
            <span class="case-date">{{ caseDetail.status }}</span>
          </div>
        </div>

        <!-- 项目背景 -->
        <div class="case-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('newEnergy.case.background') }}</h3>
            <div class="title-decoration"></div>
          </div>
          <div class="section-content">
            <p>{{ caseDetail.background }}</p>
          </div>
        </div>

        <!-- 核心挑战 -->
        <div class="case-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('newEnergy.case.challenges') }}</h3>
            <div class="title-decoration"></div>
          </div>
          <div class="challenges-grid">
            <div class="challenge-item" v-for="challenge in parsedChallenges" :key="challenge.title">
              <div class="challenge-icon">
                <i class="el-icon-time"></i>
              </div>
              <div class="challenge-content">
                <h4>{{ challenge.title }}</h4>
                <p>{{ challenge.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 专业解决方案 -->
        <div class="case-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('newEnergy.case.solution') }}</h3>
            <div class="title-decoration"></div>
          </div>
          <div class="solution-content">
            <p>{{ parsedSolution.description }}</p>
            <div class="solution-points">
              <div class="solution-point" v-for="point in parsedSolution.points" :key="point">
                <span class="point-bullet">·</span>
                <span>{{ point }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 关键操作流程 -->
        <div class="case-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('newEnergy.case.process') }}</h3>
            <div class="title-decoration"></div>
          </div>
          <div class="process-timeline">
            <div class="process-step" v-for="(step, index) in parsedProcess" :key="step.title">
              <div class="step-number">{{ index + 1 }}</div>
              <div class="step-content">
                <h4>{{ step.title }}</h4>
                <div class="step-details">
                  <div class="step-detail" v-for="detail in step.details" :key="detail">
                    <span class="detail-bullet">·</span>
                    <span>{{ detail }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 案例图片展示 -->
        <div class="case-section" v-if="parsedImages.length > 0">
          <div class="section-header">
            <h3 class="section-title">{{ $t('newEnergy.case.imageGallery') }}</h3>
            <div class="title-decoration"></div>
          </div>
          <div class="image-gallery">
            <div class="gallery-item" v-for="image in parsedImages" :key="image.src">
              <img :src="image.src" :alt="image.alt">
              <div class="image-caption">{{ image.caption }}</div>
            </div>
          </div>
        </div>

        <!-- 项目成果 -->
        <div class="case-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('newEnergy.case.results') }}</h3>
            <div class="title-decoration"></div>
          </div>
          <div class="result-content">
            <div class="result-card" v-for="result in parsedResults" :key="result.title">
              <div class="result-icon">
                <i class="el-icon-success"></i>
              </div>
              <div class="result-text">
                <h4>{{ result.title }}</h4>
                <p>{{ result.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 专业价值体现 -->
        <div class="case-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('newEnergy.case.value') }}</h3>
            <div class="title-decoration"></div>
          </div>
          <div class="value-content">
            <p>{{ caseDetail.value }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 联系我们 -->
    <CallMe />
    <MobileCallme />
  </div>
</template>

<script>
import CallMe from '@/components/callme/index'
import MobileCallme from '@/components/callme/MobileCallme.vue'
import { getNewEnergyCaseList } from '@/api/new-energy-case'

export default {
  name: 'NewEnergyCase',
  data() {
    return {
      caseDetail: {},
      loading: true,
      error: null
    }
  },
  components: {
    CallMe,
    MobileCallme
  },
  computed: {
    parsedChallenges() {
      try {
        return JSON.parse(this.caseDetail.challenges || '[]')
      } catch (e) {
        console.warn('解析挑战数据失败:', e)
        return []
      }
    },
    parsedSolution() {
      try {
        return JSON.parse(this.caseDetail.solution || '{"description": "", "points": []}')
      } catch (e) {
        console.warn('解析解决方案数据失败:', e)
        return { description: "", points: [] }
      }
    },
    parsedProcess() {
      try {
        return JSON.parse(this.caseDetail.process || '[]')
      } catch (e) {
        console.warn('解析流程数据失败:', e)
        return []
      }
    },
    parsedResults() {
      try {
        return JSON.parse(this.caseDetail.results || '[]')
      } catch (e) {
        console.warn('解析成果数据失败:', e)
        return []
      }
    },
    parsedImages() {
      try {
        return JSON.parse(this.caseDetail.images || '[]')
      } catch (e) {
        console.warn('解析图片数据失败:', e)
        return []
      }
    }
  },
  methods: {
    async loadCaseDetail() {
      try {
        this.loading = true
        this.error = null
        console.log('开始加载新能源案例列表...')
        
        // 获取案例列表，根据当前语言自动筛选
        const response = await getNewEnergyCaseList()
        console.log('新能源案例列表API响应:', response)
        
        if (response.status === 200 && response.data.data && response.data.data.length > 0) {
          // 获取列表中的第一个案例（根据当前语言自动筛选）
          this.caseDetail = response.data.data[0] || {}
          console.log('案例详情加载成功:', this.caseDetail)
        } else {
          this.error = '获取案例数据失败或暂无案例数据'
          console.error('API返回错误或无数据:', response)
        }
      } catch (err) {
        this.error = '网络错误，请稍后重试'
        console.error('获取案例数据失败:', err)
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.loadCaseDetail()
  }
}
</script>

<style lang="scss" scoped>
.new-energy-case-container {
  position: relative;
  overflow-x: hidden;
}

// 横幅区域
.banner-section {
  width: 100%;
  height: 500px;
  background: linear-gradient(135deg, rgba(26, 115, 232, 0.9), rgba(52, 211, 153, 0.8));
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  text-align: center;
  position: relative;
}

.banner-content {
  max-width: 800px;
  padding: 0 20px;
}

.banner-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.5);
}

.banner-subtitle {
  font-size: 1.3rem;
  margin-bottom: 25px;
  opacity: 0.9;
}

.banner-desc {
  font-size: 1rem;
  line-height: 1.6;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
}

// 通用容器
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

// 案例详情容器
.case-detail {
  padding: 80px 0;
  background: #f8fafc;
}

.case-header {
  text-align: center;
  margin-bottom: 60px;
}

.case-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 20px;
  line-height: 1.3;
}

.case-meta {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
}

.case-category {
  background: linear-gradient(135deg, #1a73e8, #34d399);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.case-date {
  background: #dcfce7;
  color: #166534;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

// 案例区块
.case-section {
  margin-bottom: 80px;
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
}

.section-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 15px;
  line-height: 1.3;
}

.title-decoration {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #1a73e8, #34d399);
  border-radius: 2px;
  margin: 0 auto 20px;
}

.section-content {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #374151;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
}

// 核心挑战网格
.challenges-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.challenge-item {
  background: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: flex-start;
  gap: 20px;
  transition: transform 0.3s ease;
}

.challenge-item:hover {
  transform: translateY(-5px);
}

.challenge-icon {
  background: linear-gradient(135deg, #1a73e8, #34d399);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.challenge-content h4 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 10px;
}

.challenge-content p {
  color: #64748b;
  line-height: 1.6;
}

// 解决方案
.solution-content {
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.solution-content p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #374151;
  margin-bottom: 25px;
}

.solution-points {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.solution-point {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.point-bullet {
  color: #1a73e8;
  font-weight: bold;
  font-size: 1.2rem;
  line-height: 1.4;
}

// 操作流程时间线
.process-timeline {
  display: flex;
  flex-direction: column;
  gap: 40px;
  position: relative;
}

.process-timeline::before {
  content: '';
  position: absolute;
  left: 30px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, #1a73e8, #34d399);
}

.process-step {
  display: flex;
  align-items: flex-start;
  gap: 30px;
  position: relative;
}

.step-number {
  background: linear-gradient(135deg, #1a73e8, #34d399);
  color: white;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  flex-shrink: 0;
  z-index: 1;
}

.step-content {
  flex: 1;
  background: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.step-content h4 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 20px;
}

.step-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.step-detail {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.detail-bullet {
  color: #34d399;
  font-weight: bold;
  font-size: 1.1rem;
  line-height: 1.4;
}

// 图片展示
.image-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 40px;
}

.gallery-item {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.gallery-item img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.image-caption {
  padding: 15px;
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
}

// 项目成果
.result-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.result-card {
  background: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 20px;
  transition: transform 0.3s ease;
}

.result-card:hover {
  transform: translateY(-5px);
}

.result-icon {
  background: linear-gradient(135deg, #1a73e8, #34d399);
  color: white;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.result-text h4 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.result-text p {
  color: #64748b;
  line-height: 1.6;
}

// 专业价值
.value-content {
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.value-content p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #374151;
  text-align: center;
}

// 加载状态
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 0;
  text-align: center;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1a73e8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-container p {
  font-size: 1.1rem;
  color: #64748b;
}

// 错误状态
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 0;
  text-align: center;
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 20px;
}

.error-container h3 {
  font-size: 1.5rem;
  color: #dc2626;
  margin-bottom: 15px;
}

.error-container p {
  font-size: 1rem;
  color: #64748b;
  margin-bottom: 25px;
  max-width: 400px;
}

.retry-btn {
  background: linear-gradient(135deg, #1a73e8, #34d399);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.retry-btn:hover {
  transform: translateY(-2px);
}

// 动画效果
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.new-energy-case-container > div {
  animation: fadeInUp 0.6s ease forwards;
}

// 响应式设计
@media (max-width: 768px) {
  .banner-section {
    height: 350px;
    padding: 0 20px;
  }
  
  .banner-title {
    font-size: 2rem;
  }
  
  .banner-subtitle {
    font-size: 1rem;
  }
  
  .banner-desc {
    font-size: 0.9rem;
  }
  
  .case-detail {
    padding: 50px 0;
  }
  
  .case-title {
    font-size: 1.8rem;
  }
  
  .case-section {
    margin-bottom: 50px;
  }
  
  .section-title {
    font-size: 1.5rem;
  }
  
  .challenges-grid,
  .result-content {
    grid-template-columns: 1fr;
  }
  
  .image-gallery {
    grid-template-columns: 1fr;
  }
  
  .challenge-item,
  .result-card {
    flex-direction: column;
    text-align: center;
  }
  
  .challenge-icon,
  .result-icon {
    margin-right: 0;
    margin-bottom: 15px;
  }
  
  .process-step {
    flex-direction: column;
    text-align: center;
  }
  
  .step-number {
    margin-right: 0;
    margin-bottom: 20px;
  }
  
  .section-content p,
  .solution-content p,
  .value-content p {
    font-size: 1rem;
  }
}
</style>
